/**
 * Configuration Store
 * Manages configuration state using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { configurationAPI } from '../services/configurationAPI';
import type {
  SystemConfig,
  DatabaseConfig,
  EBSConnectionConfig,
  AISPortalConnectionConfig,
  AdminPanelConnectionConfig,
  AISAdminPanelConfig,
  InvoiceProcessingConfig,
  POMatchingConfig,
  LoggingConfig,
  EmailConfig,
  NotificationConfig,
  SecurityConfig,
  APIConfig,
  ConfigValidationResult,
  ConfigHistoryEntry,
  PlatformConfig,
} from '../types/config';

// Simple logger for store operations
const logger = {
  error: (message: string, error?: any) => console.error(`[ConfigStore] ${message}`, error),
  warn: (message: string) => console.warn(`[ConfigStore] ${message}`),
  info: (message: string) => console.info(`[ConfigStore] ${message}`),
};

interface ConfigState {
  config: SystemConfig;
  isLoading: boolean;
  error: string | null;
  validationResult: ConfigValidationResult | null;
  history: ConfigHistoryEntry[];

  // Actions
  loadConfig: () => void;
  saveConfig: (changedBy?: string) => Promise<void>;
  updateDatabase: (updates: Partial<DatabaseConfig>) => void;
  updatePlatform: (updates: Partial<PlatformConfig>) => void;
  updateAISAdminPanel: (updates: Partial<AISAdminPanelConfig>) => void;
  updateEBS: (updates: Partial<EBSConnectionConfig>) => void;
  updateAISPortal: (updates: Partial<AISPortalConnectionConfig>) => void;
  updateAdminPanel: (updates: Partial<AdminPanelConnectionConfig>) => void;
  updateInvoiceProcessing: (updates: Partial<InvoiceProcessingConfig>) => void;
  updatePOMatching: (updates: Partial<POMatchingConfig>) => void;
  updateLogging: (updates: Partial<LoggingConfig>) => void;
  updateEmail: (updates: Partial<EmailConfig>) => void;
  updateNotifications: (updates: Partial<NotificationConfig>) => void;
  updateSecurity: (updates: Partial<SecurityConfig>) => void;
  updateAPI: (updates: Partial<APIConfig>) => void;
  validateConfig: () => void;
  resetToDefaults: () => Promise<void>;
  exportConfig: () => string;
  importConfig: (jsonString: string) => Promise<void>;
  getHistory: () => void;
  clearHistory: () => void;
  testDatabaseConnection: () => Promise<{ success: boolean; message: string }>;
  testEBSConnection: () => Promise<{ success: boolean; message: string }>;
  testAPIConnection: () => Promise<{ success: boolean; message: string }>;
  setError: (error: string | null) => void;
  clearError: () => void;
}

// Default configuration
const getDefaultConfig = (): SystemConfig => ({
  platform: {
    selectedPlatform: 'EBS',
    platformName: 'Oracle E-Business Suite',
    platformVersion: '12.2',
    platformDescription: 'Oracle EBS 12.x',
  },
  database: {
    type: 'oracle',
    oracle: {
      host: 'localhost',
      port: 1521,
      connectionType: 'SID',
      sidOrServiceName: 'ORCL',
      username: 'apps',
      password: '',
      ssl: false,
      poolSize: 10,
      connectionTimeout: 30000,
      idleTimeout: 30000,
    },
  },
  ebs: {
    host: 'localhost',
    port: 8000,
    username: 'admin',
    password: '',
    instanceName: 'EBS',
    responsibility: 'System Administrator',
    ssl: false,
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
  },
  aisPortal: {
    baseUrl: 'http://localhost:8080',
    apiKey: '',
    apiSecret: '',
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
  },
  adminPanel: {
    baseUrl: 'https://admin.yourdomain.com',
    apiKey: '',
    apiSecret: '',
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
  },
  aisAdminPanel: {
    baseUrl: 'https://ais-admin.yourdomain.com',
    apiKey: '',
    apiSecret: '',
    platformCode: 'ORCL-EBS',
    versionControl: '1.0.0',
    retryPolicy: {
      maxRetries: 3,
      retryDelay: 1000,
      backoffMultiplier: 2,
    },
    webhookUrl: '',
    webhookSecret: '',
    errorMessageTemplates: true,
  },
  invoiceProcessing: {
    arInvoiceProcessing: {
      enabled: true,
      batchSize: 100,
      processingInterval: 60,
      retryAttempts: 3,
      retryDelay: 5,
      timeout: 300,
      ublMapping: {
        enabled: true,
        mappingTemplate: 'standard',
      },
      ftaCompliance: {
        enabled: true,
        complianceLevel: 'FULL',
      },
      responseFieldMapping: {
        statusField: 'invoice_status',
        irnField: 'irn',
        hashField: 'hash',
        qrField: 'qr_code',
        uuidField: 'uuid',
      },
    },
    apInvoiceProcessing: {
      enabled: true,
      batchSize: 100,
      processingInterval: 60,
      retryAttempts: 3,
      retryDelay: 5,
      timeout: 300,
      autoCreateAPInvoice: true,
      approvalWorkflow: 'standard',
      irnHashStorage: {
        enabled: true,
        storageLocation: 'invoice_header',
      },
    },
  },
  poMatching: {
    enabled: true,
    matchingThreshold: 95,
    supplierMatchWeight: 30,
    amountMatchWeight: 40,
    lineItemsMatchWeight: 20,
    dateMatchWeight: 10,
    autoMatch: true,
    autoMatchThreshold: 98,
  },
  logging: {
    level: 'info',
    format: 'json',
    maxFileSize: 100,
    maxFiles: 10,
    retentionDays: 30,
    enableConsoleLogging: true,
    enableFileLogging: true,
  },
  email: {
    enabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromAddress: '',
    fromName: '',
    useTLS: true,
    useSSL: false,
  },
  notifications: {
    email: {
      enabled: false,
      onProcessingError: true,
      onProcessingSuccess: false,
      onMatchingIssue: true,
      recipients: [],
    },
    webhook: {
      enabled: false,
      url: '',
      events: [],
      retryAttempts: 3,
      retryDelay: 1000,
    },
  },
  security: {
    jwtSecret: 'your-secret-key-min-32-characters-required',
    jwtExpiration: 24,
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireLowercase: true,
    passwordRequireNumbers: true,
    passwordRequireSpecialChars: true,
    maxLoginAttempts: 5,
    lockoutDuration: 15,
    enableMFA: false,
  },
  api: {
    baseUrl: 'http://localhost:3000',
    timeout: 30,
    retryAttempts: 3,
    retryDelay: 1,
    enableCORS: true,
    corsOrigins: ['http://localhost:5173', 'http://localhost:3000'],
    rateLimitEnabled: true,
    rateLimitRequests: 100,
    rateLimitWindow: 60,
  },
  lastUpdated: new Date().toISOString(),
  updatedBy: 'system',
});

export const useConfigStore = create<ConfigState>()(
  persist(
    (set, get) => ({
      config: getDefaultConfig(),
      isLoading: false,
      error: null,
      validationResult: null,
      history: [],

      loadConfig: async () => {
        try {
          set({ isLoading: true });
          // Try to load from backend first
          const config = await configurationAPI.getConfiguration();
          set({ config, error: null, isLoading: false });
        } catch (error) {
          logger.warn('Failed to load configuration from backend, using defaults');
          set({ 
            config: getDefaultConfig(), 
            error: null, 
            isLoading: false 
          });
        }
      },

      saveConfig: async (changedBy = 'user') => {
        try {
          set({ isLoading: true });
          const { config } = get();
          
          // Save to backend
          const savedConfig = await configurationAPI.saveConfiguration(config);
          set({ config: savedConfig, isLoading: false, error: null });
        } catch (error) {
          set({ isLoading: false, error: error instanceof Error ? error.message : 'Failed to save configuration' });
          throw error;
        }
      },

      updateDatabase: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            database: { ...state.config.database, ...updates },
          },
        }));
      },

      updatePlatform: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            platform: { ...state.config.platform, ...updates },
          },
        }));
      },

      updateAISAdminPanel: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            aisAdminPanel: { ...state.config.aisAdminPanel, ...updates },
          },
        }));
      },

      updateEBS: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            ebs: { ...state.config.ebs, ...updates },
          },
        }));
      },

      updateAISPortal: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            aisPortal: { ...state.config.aisPortal, ...updates },
          },
        }));
      },

      updateAdminPanel: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            adminPanel: { ...state.config.adminPanel, ...updates },
          },
        }));
      },

      updateInvoiceProcessing: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            invoiceProcessing: { ...state.config.invoiceProcessing, ...updates },
          },
        }));
      },

      updatePOMatching: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            poMatching: { ...state.config.poMatching, ...updates },
          },
        }));
      },

      updateLogging: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            logging: { ...state.config.logging, ...updates },
          },
        }));
      },

      updateEmail: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            email: { ...state.config.email, ...updates },
          },
        }));
      },

      updateNotifications: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            notifications: { ...state.config.notifications, ...updates },
          },
        }));
      },

      updateSecurity: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            security: { ...state.config.security, ...updates },
          },
        }));
      },

      updateAPI: (updates) => {
        set((state) => ({
          config: {
            ...state.config,
            api: { ...state.config.api, ...updates },
          },
        }));
      },

      validateConfig: () => {
        const { config } = get();
        // Validation will be done on backend
        set({ validationResult: { valid: true, errors: [], warnings: [] } });
      },

      resetToDefaults: async () => {
        try {
          set({ isLoading: true });
          const config = await configurationAPI.resetConfiguration();
          set({ config, isLoading: false, error: null });
        } catch (error) {
          set({ isLoading: false, error: error instanceof Error ? error.message : 'Failed to reset configuration' });
          throw error;
        }
      },

      exportConfig: () => {
        const { config } = get();
        return JSON.stringify(config, null, 2);
      },

      importConfig: async (jsonString) => {
        try {
          set({ isLoading: true });
          const config = JSON.parse(jsonString);
          const savedConfig = await configurationAPI.importConfiguration(config);
          set({ config: savedConfig, isLoading: false, error: null });
        } catch (error) {
          set({ isLoading: false, error: error instanceof Error ? error.message : 'Failed to import configuration' });
          throw error;
        }
      },

      getHistory: () => {
        configurationAPI.getConfigurationHistory()
          .then(history => {
            set({ history });
          })
          .catch(error => {
            logger.error('Failed to get configuration history', error);
          });
      },

      clearHistory: () => {
        configurationAPI.clearConfigurationHistory()
          .then(() => {
            set({ history: [] });
          })
          .catch(error => {
            logger.error('Failed to clear configuration history', error);
          });
      },

      testDatabaseConnection: async () => {
        const { config } = get();
        return configurationAPI.testDatabaseConnection(config.database);
      },

      testEBSConnection: async () => {
        const { config } = get();
        return configurationAPI.testEBSConnection(config.ebs);
      },

      testAPIConnection: async () => {
        const { config } = get();
        return configurationAPI.testAPIConfiguration(config.api);
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'config-store',
    }
  )
);
